import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Wordmark } from "@/components/Icon";
import { cx } from "@/components/ui";
import { LOCALES, setLocale } from "@/i18n";
import { useThemeMode } from "@/lib/theme";
import type { Locale } from "@/api/types/Locale";

/** Centered card for the sign-in flow. Follows the OS theme (no user yet). */
export function AuthLayout({ step, children }: { step?: 1 | 2 | 3; children: ReactNode }) {
  useThemeMode("system");
  const { t, i18n } = useTranslation();

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-8">
      <div className="card flex w-full max-w-[460px] flex-col gap-6 p-6 sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Wordmark className="text-[24px]" />
          </div>
          <label className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-text-2">
            <Icon name="globe" size={14} />
            <span className="sr-only">{t("auth.language")}</span>
            <select
              value={i18n.language}
              onChange={(e) => setLocale(e.target.value as Locale)}
              className="cursor-pointer bg-transparent outline-offset-4"
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {t(`locales.${l}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {step && <Steps current={step} />}
        {children}
      </div>
    </main>
  );
}

function Steps({ current }: { current: 1 | 2 | 3 }) {
  const { t } = useTranslation();
  const labels = [t("auth.steps.password"), t("auth.steps.authenticator"), t("auth.steps.recovery")];
  return (
    <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
      {labels.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const cur = n === current;
        return (
          <li key={label} className="contents">
            {i > 0 && <span aria-hidden="true" className="h-px min-w-3 flex-1 bg-border" />}
            <span
              aria-current={cur ? "step" : undefined}
              className={cx("inline-flex items-center gap-1.5 text-xs font-semibold", cur ? "text-text" : "text-muted")}
            >
              <i
                className={cx(
                  "inline-flex size-5 items-center justify-center rounded-full text-[12px] not-italic",
                  done ? "bg-pass-bg text-pass-fg" : cur ? "bg-accent text-accent-fg" : "bg-surface-2",
                )}
              >
                {done ? <Icon name="check" size={12} strokeWidth={2.6} /> : n}
              </i>
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
